// Approved client copy for the Figma edit dialog (PAD-32396). Keep it verbatim: never "Flyer Builder", no dashes.

export const FIGMA_EDIT_TITLE = "Before you edit this design";

export const FIGMA_EDIT_SENTENCES = {
  imported: "This design was imported from Figma.",
  flattensPlaceholders:
    "Opening it in the design tool flattens the artwork into a single image, so any placeholders you added in Figma will be lost, including text fields, QR codes and the address box.",
  replacesImport:
    "Opening it in the design tool converts the artwork into a flat image, which replaces the file you imported.",
  copyKeepsOriginal: "Editing a copy keeps the original as it is.",
  usedByCampaign: "Because a campaign is already using this design, make a copy to edit it.",
} as const;

export const FIGMA_EDIT_BUTTONS = {
  flattenOriginal: "Flatten and edit original",
  editCopy: "Edit a copy",
  cancel: "Cancel",
} as const;

export const FIGMA_EDIT_ERROR = "We could not prepare this design for editing. Please try again.";

// Not in the approved ticket copy: shown only when the design lookup fails before the dialog can be chosen.
export const FIGMA_EDIT_LOOKUP_ERROR = {
  title: "We could not open this design",
  body: "Please check your connection and try again.",
  retry: "Try again",
} as const;

export interface FigmaEditCopyInput {
  readonly hasPlaceholders: boolean;
  readonly usedByCampaign: boolean;
}

export type FigmaEditSecondaryAction = "flatten" | "cancel";

export interface FigmaEditCopy {
  readonly title: string;
  readonly body: string;
  readonly secondaryLabel: string;
  readonly secondaryAction: FigmaEditSecondaryAction;
  readonly primaryLabel: string;
}

export const composeFigmaEditBody = ({ hasPlaceholders, usedByCampaign }: FigmaEditCopyInput): string => {
  // Sentence 2 depends on the placeholders only, so the body is always true for the design shown (ticket rule).
  const consequence = hasPlaceholders ? FIGMA_EDIT_SENTENCES.flattensPlaceholders : FIGMA_EDIT_SENTENCES.replacesImport;
  const closing = usedByCampaign ? FIGMA_EDIT_SENTENCES.usedByCampaign : FIGMA_EDIT_SENTENCES.copyKeepsOriginal;
  return [FIGMA_EDIT_SENTENCES.imported, consequence, closing].join(" ");
};

export const getFigmaEditCopy = (input: FigmaEditCopyInput): FigmaEditCopy => ({
  title: FIGMA_EDIT_TITLE,
  body: composeFigmaEditBody(input),
  secondaryLabel: input.usedByCampaign ? FIGMA_EDIT_BUTTONS.cancel : FIGMA_EDIT_BUTTONS.flattenOriginal,
  secondaryAction: input.usedByCampaign ? "cancel" : "flatten",
  primaryLabel: FIGMA_EDIT_BUTTONS.editCopy,
});

// An unknown campaign usage is treated as in use, so flattening is only offered when the server says it is safe.
export const toFigmaEditCopyInput = (design: {
  readonly hasFigmaPlaceholders?: boolean | null;
  readonly usedByCampaign?: boolean | null;
}): FigmaEditCopyInput => ({
  hasPlaceholders: design.hasFigmaPlaceholders === true,
  usedByCampaign: design.usedByCampaign !== false,
});
